/**
 * @fileoverview Главный тик симуляции смены.
 */

import { Snd } from "../audio/sound";
import { canLand, cmdLand } from "../commands/ops";
import { frontStage } from "../state/createGame";
import type { GameHooks, GameState, Plane } from "../state/types";
import { clamp, lerp, rnd } from "../util/math";
import { maybeRequireDeice, updatePlaneCare } from "./deice";
import { nextEventDelay, pushDecision } from "./events";
import { updateFront } from "./front";
import { updateGroundTraffic } from "./groundTraffic";
import { startHazard, updateHazards } from "./hazards";
import { updateRwyCare } from "./rwyCare";
import { tickSafety } from "./safety";
import { spawnPlane } from "../plane/spawn";
import { updatePlane } from "./updatePlane";
import { weatherEvent } from "./weather";

export interface TickResult {
  /** Снять выделение с борта */
  deselect?: boolean;
  /** Подпись фронта для HUD */
  frontLabel: string;
}

/**
 * Один кадр логики (без отрисовки и без DOM).
 * @param selected — текущий выбранный борт (для сброса после crash)
 */
export function tickSimulation(
  g: GameState,
  hooks: GameHooks,
  dt: number,
  selected: Plane | null,
): TickResult {
  if (!g.running) {
    return { frontLabel: `${frontStage(g).name}` };
  }

  g.elapsed += dt;
  g.timeAlive += dt;

  if (g.mode === "play") {
    if (!g.cfg.endless) {
      g.time -= dt;
      if (g.time <= 0) {
        g.time = 0;
        hooks.endGame(
          "Смена завершена",
          "Метель отступает. Вы передаете дежурство.",
        );
        return { frontLabel: `${frontStage(g).name}` };
      }
    }
  }

  const phase =
    g.mode === "tutorial"
      ? 0
      : g.cfg.endless
        ? Math.min(2, g.front.wave - 1)
        : g.elapsed < g.cfg.time / 3
          ? 0
          : g.elapsed < (2 * g.cfg.time) / 3
            ? 1
            : 2;

  if (g.snowBoostT > 0) {
    g.snowBoostT -= dt;
    if (g.snowBoostT <= 0) {
      g.snowBoost = 0;
      g.wxName = "Умеренный снег";
      hooks.onWxName?.(g.wxName);
    }
  }
  if (g.crossT > 0) {
    g.crossT -= dt;
    if (g.crossT <= 0) {
      g.cross = 0;
      g.windSpd = rnd(8, 15);
      g.wxName = "Умеренный снег";
      hooks.onWxName?.(g.wxName);
    }
  }
  if (g.visT > 0) g.visT -= dt;
  if (g.takeoffLockT > 0) g.takeoffLockT = Math.max(0, g.takeoffLockT - dt);
  g.vis = lerp(g.vis, g.visTarget + phase * 0.04, dt * 0.5);
  g.windDir += Math.sin(g.elapsed * 0.13) * dt * 0.05;

  if (g.mode === "play") {
    g.eventT -= dt;
    if (g.eventT <= 0) {
      if (Math.random() < 0.55) pushDecision(g, hooks);
      else weatherEvent(g, hooks);
      g.eventT = nextEventDelay(phase, g.cfg.events);
    }
  }

  Snd.wind(
    clamp(g.windSpd / 34, 0, 1) * (1 + g.snowBoost * 0.4) +
      frontStage(g).dens * 0.08,
  );

  if (g.clearLockT > 0) g.clearLockT = Math.max(0, g.clearLockT - dt);
  updateFront(g, hooks, dt);
  updateRwyCare(g, hooks, dt);
  updatePlaneCare(g, hooks, dt);

  for (const r of g.runways) {
    r.snowCool = Math.max(0, (r.snowCool || 0) - dt);
    if (r.reservedFor != null || (r.reserveT || 0) > 0) {
      const owner = g.planes.find((p) => p.id === r.reservedFor && !p.dead);
      const stillNeeds =
        owner &&
        (owner.status === "approach" ||
          owner.status === "inbound" ||
          owner.status === "landing");
      if (stillNeeds) {
        r.reserveT = Math.max(r.reserveT || 0, 1);
        // Медрейс ждет на орбите — как только полоса свободна, даем заход сами
        if (
          owner.status === "approach" &&
          canLand(owner, r).ok
        ) {
          cmdLand(g, hooks, owner, r.id, { force: true });
          hooks.toast(
            `${owner.cs}: полоса свободна — заход на ${r.name}`,
            "good",
          );
        }
      } else if ((r.reserveT || 0) > 0) {
        r.reserveT -= dt;
        if (r.reserveT <= 0) {
          r.reserveT = 0;
          r.reservedFor = null;
          r.reserveLabel = "";
          hooks.toast(
            `${r.name}: резерв под медрейс снят — полоса снова общая`,
            "wx",
          );
        }
      } else if (!owner) {
        r.reservedFor = null;
        r.reserveLabel = "";
      }
    }
    if (r.tempCloseT > 0) {
      r.tempCloseT -= dt;
      r.closed = true;
      if (r.tempCloseT <= 0) {
        r.tempCloseT = 0;
        if (!(r.clearSnowT > 0)) {
          r.closed = false;
          hooks.toast(`${r.name} снова открыта после сбоя огней`, "good");
        }
      }
    }
    if (r.snow < 70) r.critArmed = true;
    if (
      r.snow >= 90 &&
      !r.closed &&
      r.critArmed !== false &&
      r.snowCool <= 0 &&
      !g.hazards.some((h) => h.kind === "snow" && h.r === r)
    ) {
      r.critArmed = false;
      r.snowCool = 40;
      startHazard(g, hooks, "snow", null, r, 14);
      hooks.toast(`Критический занос ${r.name} — нужна уборка!`, "bad");
    }
  }

  if (g.mode === "play") {
    g.spawnT -= dt * g.cfg.spawn;
    const cap =
      (phase === 0 ? 5 : phase === 1 ? 7 : 9) +
      (g.cfg.endless ? Math.min(3, g.front.wave - 1) : 0);
    const waitN = g.planes.filter(
      (p) => !p.dead && p.status === "awaiting_gate",
    ).length;
    const gatesFull = g.gates.every((gate) => gate.plane && !gate.plane.dead);
    const jam = waitN >= 2 && gatesFull;
    if (jam) {
      g.paxMood = Math.max(0, g.paxMood - dt * 2);
      if (!g._apronWarn || g.elapsed - g._apronWarn > 14) {
        g._apronWarn = g.elapsed;
        hooks.toast(
          `Очередь на гейты: ${waitN} борт(а) ждут · все гейты заняты`,
          "bad",
        );
        g.delays++;
      }
    }
    if (
      g.spawnT <= 0 &&
      !jam &&
      g.planes.filter((p) => !p.dead).length < cap
    ) {
      spawnPlane(g, hooks, null, Math.random() < (phase === 2 ? 0.14 : 0.05));
      g.spawnT =
        phase === 0
          ? rnd(8.5, 12)
          : phase === 1
            ? rnd(6.5, 9)
            : rnd(4.6, 6.6);
    } else if (g.spawnT <= 0) {
      g.spawnT = 2.5;
    }
  }

  for (const p of g.planes) {
    if (!p.dead) {
      maybeRequireDeice(g, hooks, p);
      updatePlane(g, hooks, p, dt, phase);
    }
  }

  updateGroundTraffic(g, hooks, dt);
  updateHazards(g, hooks, dt);
  const { frontLabel } = tickSafety(g, hooks.endGame);

  g.planes = g.planes.filter((p) => !p.dead || p.crashed);
  const deselect = !!(selected && selected.dead);

  for (let i = g.floats.length - 1; i >= 0; i--) {
    const f = g.floats[i]!;
    f.t -= dt;
    f.y -= dt * 26;
    if (f.t <= 0) g.floats.splice(i, 1);
  }
  for (let i = g.sparks.length - 1; i >= 0; i--) {
    const s = g.sparks[i]!;
    s.t -= dt;
    s.x += s.vx * dt;
    s.y += s.vy * dt;
    s.vy += 180 * dt;
    s.vx *= 0.98;
    if (s.t <= 0) g.sparks.splice(i, 1);
  }
  g.flash = Math.max(0, g.flash - dt * 1.4);
  g.shake = Math.max(0, g.shake - dt * 1.6);
  for (const r of g.runways) r.alert = Math.max(0, r.alert - dt);

  return { deselect, frontLabel };
}
